import { useIntl, useLogout, UserCredentials, useServices, useSettings } from '@capsicum/common';
import { env, type MenuItemProps, useDevItems, useDrawer } from '@capsicum/common-web';
import {
	BundleTicketIcon,
	ChevronRightIcon,
	CollectibleIcon,
	HistoryIcon,
	IdentityIcon,
	LogoutIcon,
	MarketplaceWithCircleIcon,
	QuestPostIcon,
	SettingsIcon,
	SociosIcon
} from '@capsicum/common-web/icons';
import { useGitLabTag } from '@capsicum/devkit/shared';
import { useRequirementsPage } from '@capsicum/features/regulations/shared';
import { TabNames } from '@capsicum/features/wallet/shared';
import { RequirementsPageCredentialsNeeded } from '@capsicum/socios-core';
import { useCredentialsHandler } from '@capsicum/socios-core-web';
import { useCallback } from 'react';

type UseDrawerContentProps = {
	isDev?: boolean;
	useRouter: () => { push: (route: string) => void };
};

export const useDrawerContent = ({ isDev, useRouter }: UseDrawerContentProps) => {
	const { $t } = useIntl();
	const { closeDrawer } = useDrawer();
	const services = useServices();
	const logout = useLogout();
	const router = useRouter();
	const { settings } = useSettings();
	const { personalVerificationMenuItemBadgeColor } = useRequirementsPage();
	const { isAdvancedTradingEnabled, isDigitalCollectiblesEnabled, isVoucherEnabled } = settings || {};
	const { link: tagLink, title: tagTitle } = useGitLabTag(env.NEXT_PUBLIC_TAG_VERSION, env.NEXT_PUBLIC_INT_SHA);
	const { handleCredential: onClickRedeemVoucher } = useCredentialsHandler({
		credentialCodes: UserCredentials.VOTE_TOKEN_REDEEM,
		onCredentialGranted: () => navigate('/wallet/voucher'),
		page: RequirementsPageCredentialsNeeded.voucherRedemption
	});

	const devItems = useDevItems();

	const navigate = useCallback(
		(route: string) => {
			router.push(route);
			closeDrawer();
		},
		[router, closeDrawer]
	);

	const versionData = tagLink
		? {
				onClick: () => window.open(tagLink, '_blank'),
				title: tagTitle ?? ''
			}
		: undefined;

	const items = [
		// Common items that are always included
		{
			icon: SettingsIcon,
			isIconReversible: false,
			isTrailingIconReversible: true,
			onClick: () => navigate('/settings'),
			testID: 'sideDrawerMenu-settings-link',
			title: $t('menu.settings'),
			trailingIcon: ChevronRightIcon
		},
		{
			icon: IdentityIcon,
			isIconReversible: false,
			isTrailingIconReversible: true,
			labelBadgeVariant: personalVerificationMenuItemBadgeColor,
			onClick: () => navigate('/personal-verification'),
			testID: 'sideDrawerMenu-personalVerification-link',
			title: $t('profile.personalVerification'),
			trailingIcon: ChevronRightIcon
		},
		isVoucherEnabled && {
			icon: BundleTicketIcon,
			isIconReversible: false,
			isTrailingIconReversible: true,
			onClick: () => {
				onClickRedeemVoucher();
				closeDrawer();
			},
			testID: 'sideDrawerMenu-voucherRedemption-link',
			title: $t('wallet.voucher.redeemVoucher'),
			trailingIcon: ChevronRightIcon
		},
		isDigitalCollectiblesEnabled && {
			icon: CollectibleIcon,
			isIconReversible: false,
			isTrailingIconReversible: true,
			onClick: () => navigate(`/wallet?tab=${TabNames.DIGITAL_COLLECTIBLES}`),
			testID: 'sideDrawerMenu-digitalCollectibles-link',
			title: $t('menu.digitalCollectibles'),
			trailingIcon: ChevronRightIcon
		},
		isAdvancedTradingEnabled && {
			icon: MarketplaceWithCircleIcon,
			isIconReversible: false,
			isTrailingIconReversible: true,
			onClick: () => navigate(`/blockchain-and-trading`),
			testID: 'sideDrawerMenu-digitalCollectibles-link',
			title: $t('profile.advancedTrading'),
			trailingIcon: ChevronRightIcon
		},
		{
			icon: QuestPostIcon,
			isIconReversible: false,
			isTrailingIconReversible: true,
			onClick: () => navigate('/support'),
			testID: 'sideDrawerMenu-support-link',
			title: $t('menu.support'),
			trailingIcon: ChevronRightIcon
		},
		{
			icon: SociosIcon,
			isIconReversible: false,
			isTrailingIconReversible: true,
			onClick: () => navigate('/about-us'),
			testID: 'sideDrawerMenu-aboutUs-link',
			title: $t('menu.aboutUs'),
			trailingIcon: ChevronRightIcon
		},
		{
			icon: LogoutIcon,
			isIconReversible: true,
			onClick: () => {
				services.setUserId('');
				logout();
				closeDrawer();
			},
			testID: 'sideDrawerMenu-logout-link',
			title: $t('menu.logout')
		}
	] as Array<MenuItemProps>;

	if (['staging', 'integration', 'integration-us', 'staging-us'].includes(env.NEXT_PUBLIC_ENV ?? '') && versionData) {
		items.push({
			icon: HistoryIcon,
			onClick: () => versionData.onClick(),
			testID: 'sideDrawerMenu-version-link',
			title: versionData.title
		} as unknown as MenuItemProps);
	}

	if (isDev) {
		items.push(...devItems);
	}

	return {
		items: items.filter(Boolean) as Array<MenuItemProps>,
		redirectTo: navigate
	};
};
